/**
 * RBAC-5.3 — degraded accounts and recovery (`20261102a5`).
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the rows mean is `lib/accountRecovery.ts`; who may call what is
 * the database's. The caller-facing calls pass no tenant id. Errors carry the server's sentence and
 * hint, which are written for the person reading them.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  DEGRADED_ACCOUNTS_RPC,
  MY_ACCOUNT_ADMINISTRATION_RPC,
  RECOVER_ACCOUNT_ADMINISTRATION_RPC,
  REQUEST_ACCOUNT_RECOVERY_RPC,
  RESOLVE_ACCOUNT_RECOVERY_REQUEST_RPC,
  parseDegradedAccounts,
  parseMyAccountAdministration,
  parseRecoveryResult,
  type DegradedAccount,
  type MyAccountAdministration,
  type RecoveryResult,
} from '@/lib/accountRecovery';
import type { RpcErrorLike } from '@/lib/roleDuplication';

export class AccountRecoveryError extends Error {
  constructor(public readonly rpc: RpcErrorLike) {
    super(rpc.message ?? 'Unknown error');
  }
}

const toError = (e: { message?: string; code?: string; hint?: string }) =>
  new AccountRecoveryError({ message: e.message, code: e.code, hint: e.hint });

/** Null when the answer cannot be read: the blocked surface then says only what it knows. */
export async function fetchMyAccountAdministration(): Promise<MyAccountAdministration | null> {
  const { data, error } = await supabase.rpc(MY_ACCOUNT_ADMINISTRATION_RPC);
  if (error) throw toError(error);
  return parseMyAccountAdministration(data);
}

export async function requestAccountRecovery(note: string | null): Promise<{ createdAt: string | null; alreadyOpen: boolean }> {
  const { data, error } = await supabase.rpc(REQUEST_ACCOUNT_RECOVERY_RPC, { p_note: note && note.trim() ? note.trim() : null });
  if (error) throw toError(error);
  const row = (data ?? {}) as { created_at?: unknown; already_open?: unknown };
  return {
    createdAt: typeof row.created_at === 'string' ? row.created_at : null,
    alreadyOpen: row.already_open === true,
  };
}

export async function fetchDegradedAccounts(): Promise<DegradedAccount[]> {
  const { data, error } = await supabase.rpc(DEGRADED_ACCOUNTS_RPC);
  if (error) throw toError(error);
  return parseDegradedAccounts(data);
}

export async function recoverAccountAdministration(tenantId: string, userId: string): Promise<RecoveryResult> {
  const { data, error } = await supabase.rpc(RECOVER_ACCOUNT_ADMINISTRATION_RPC, { p_tenant_id: tenantId, p_user_id: userId });
  if (error) throw toError(error);
  const parsed = parseRecoveryResult(data);
  if (!parsed) throw new AccountRecoveryError({ message: 'The recovery went through, but its answer came back in a shape this screen does not read.' });
  return parsed;
}

export async function resolveAccountRecoveryRequest(requestId: number, resolution: string): Promise<void> {
  const { error } = await supabase.rpc(RESOLVE_ACCOUNT_RECOVERY_REQUEST_RPC, { p_request_id: requestId, p_resolution: resolution });
  if (error) throw toError(error);
}

/** The server's sentence and its hint, as one line for a toast or an inline error. */
export function recoveryErrorText(e: unknown): string {
  if (e instanceof AccountRecoveryError) {
    return [e.rpc.message, e.rpc.hint].filter(Boolean).join(' ');
  }
  return e instanceof Error ? e.message : 'Something went wrong.';
}
